/**
 * Layout wrapper component for creating dynamic row-column layouts with padding around the elements.
 *
 * The `DynamicLayout` component handles spacing the layout on the top and left side; the `DynamicLayoutColumn` component handles bottom
 * and right spacing. This is so that each element keeps the correct padding when it wraps.
 *
 * Example ("|" and "-" represent padding):
 *
 * Markup:
 *<DynamicLayout>
 *  <DynamicLayoutRow>
 *    <DynamicLayoutColumn>element 1</DynamicLayoutColumn>
 *    <DynamicLayoutColumn>element 2</DynamicLayoutColumn>
 *  </DynamicLayoutRow>
 *  <DynamicLayoutRow>
 *    <DynamicLayoutColumn>element 3</DynamicLayoutColumn>
 *    <DynamicLayoutColumn>element 4</DynamicLayoutColumn>
 *  </DynamicLayoutRow>
 * </DynamicLayout>
 *
 * Full screen:
 * -------------------
 * |[element 1]|[element 2]|
 * -------------------
 * |[element 3]|[element 4]|
 * -------------------
 *
 * Half screen:
 * ----------
 * |[element 1]|
 * ----------
 * |[element 2]|
 * ----------
 * |[element 3]|
 * ----------
 * |[element 4]|
 * ----------
 */

import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import {assert} from 'Asserts';
import getTestProps from 'test/lib/cypress/getTestProps';

// The amount of spacing around each element (px)
export const DYNAMIC_LAYOUT_SPACING = 12;

interface DynamicLayoutRowProps {
  // One or more DynamicLayoutColumn elements with widthProportion props summing to 1
  children: ReactNode;
  height?: number | string;
}

/**
 * Row component for creating row-column layouts with `DynamicLayout`. The component expects children as a prop to
 * define individual columns. Children should be `DynamicLayoutColumn` components. The children will expand
 * to fill the height of the component, which is specified by the height prop.
 *
 * Example ("|" and "-" represent padding):
 *
 * Markup:
 *
 *<DynamicLayoutRow>
 *   <DynamicLayoutColumn>element 1</DynamicLayoutColumn>
 *   <DynamicLayoutColumn>element 2</DynamicLayoutColumn>
 *   <DynamicLayoutColumn>element 3</DynamicLayoutColumn>
 *</DynamicLayoutRow>
 *
 * Full screen:
 * ----------------------------
 * |[element 1]|[element 2]|[element 3]|
 * ----------------------------
 *
 * Smaller screen:
 * -------------------
 * |[element 1]|[element 2]|
 * -------------------
 * |[element             3]|
 * -------------------
 *
 * Smallest screen:
 * -----------
 * |[element 1]|
 * -----------
 * |[element 2]|
 * -----------
 * |[element 3]|
 * -----------
 */
export function DynamicLayoutRow({children, height}: DynamicLayoutRowProps): JSX.Element {
  // Assuming that rows are always full allows us to use flex box + flex-basis + min-width to make the
  // columns responsive and wrap. The columns always have flex-grow, which makes them fill the row. If we
  // did not make this assumption, it could be expected that are able to set partial rows with this component
  // (this is how Bootstrap Row/Col works for example), and then we would need to use media queries to control
  // the wrapping. This is a problem because media queries only let use define a min-width of elements based
  // on the screen size and not the div size, which leads to inconsistent min-widths for columns.
  // We should ensure that the total proportion of the child columns adds up to 1.
  const totalProportion = React.Children.toArray(children).reduce((accumulatedProportion, column) => {
    // Ensure caller doesn't pass in a string
    assert(React.isValidElement(column), 'Expected children to be valid elements');
    return accumulatedProportion + column.props.widthProportion;
  }, 0);

  assert(totalProportion === 1, `Expected total width proportions of column definitions to be equal to 1.`, {
    totalProportion,
  });

  return (
    <div className={css(styles.row)} style={{height}}>
      {children}
    </div>
  );
}

export interface DynamicLayoutColumnProps {
  children: ReactNode;
  widthProportion: number;
}

/**
 * Column component to be used as the child of `DynamicLayoutRow`.
 *
 * @param widthProportion What percentage of the parent row's width should this component take.
 * The widthProportion should be between 0 and 1.
 */
export function DynamicLayoutColumn({children, widthProportion}: DynamicLayoutColumnProps): JSX.Element {
  return (
    <div
      className={css(styles.column)}
      style={{flexBasis: `${widthProportion * 100}%`}}
      {...getTestProps({component: 'DynamicLayoutColumn', element: 'wrapper'})}
    >
      {children}
    </div>
  );
}

export function OneColumnDynamicLayoutRow({children, height}: DynamicLayoutRowProps): JSX.Element {
  return (
    <DynamicLayoutRow height={height}>
      <DynamicLayoutColumn widthProportion={1}>{children}</DynamicLayoutColumn>
    </DynamicLayoutRow>
  );
}

export interface DynamicLayoutProps {
  children: ReactNode;
}

/**
 * The component expects children as a prop to define individual rows.
 * Rows should be `DynamicLayoutRow` or `OneColumnDynamicLayoutRow` components and columns should be `DynamicLayoutColumn` components.
 * Otherwise, the spacing will not be as expected.
 */
export default function DynamicLayout({children}: DynamicLayoutProps): JSX.Element {
  return <div className={css(styles.layout)}>{children}</div>;
}

const styles = StyleSheet.create({
  layout: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    paddingLeft: DYNAMIC_LAYOUT_SPACING,
    paddingTop: DYNAMIC_LAYOUT_SPACING,
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  column: {
    flexShrink: 1,
    flexGrow: 1,
    minWidth: 300,
    height: '100%',
    paddingRight: DYNAMIC_LAYOUT_SPACING,
    paddingBottom: DYNAMIC_LAYOUT_SPACING,
  },
});
